import { attributeDefinitionIdFormat } from "@redotech/redo-ids/ids";
import {
  AttributeType,
  AttributeValueSchema,
  AttributeValueType,
  createSafeNullableEnumSchema,
  HsCodeSchema,
  MoneySchema,
} from "@redotech/redo-inventory-common/attributes";
import {
  CountryCodeSchema,
  ShopifyWeightSchema,
  UnitPriceMeasurementMeasuredUnitSchema,
} from "@redotech/redo-inventory-common/shopify-listing/common-schemas";
import { ShopifyVariantFieldType } from "@redotech/redo-prisma-client/enums";
import { ShopifyIdType } from "@redotech/shopify-util/id";
import { shopifyGidSchema } from "@redotech/shopify-util/id-schema";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { BaseProductListingSchema, ListingType } from "../base-listing";
import {
  ShopifyListingMediaSchema,
  ShopifyListingMediaWithoutRedoIds,
  ShopifyListingMediaWithShopifyIds,
} from "./shopify-media";
import { ShopifyMetafieldSchema } from "./shopify-metafields";

export enum ShopifyInventoryPolicyEnum {
  CONTINUE = "CONTINUE",
  DENY = "DENY",
}
export const ShopifyInventoryPolicySchema = createEnum(
  ShopifyInventoryPolicyEnum,
);

const ShopifyListingSelectedOptionSchema_ = z.object({
  name: z.string(),
  value: z.string(),
});
export const ShopifyListingSelectedOptionSchema: z.ZodType<ShopifyListingSelectedOption> =
  ShopifyListingSelectedOptionSchema_;
export interface ShopifyListingSelectedOption extends z.infer<
  typeof ShopifyListingSelectedOptionSchema_
> {}

const UnitPriceMeasurementSchema_ = z.object({
  measuredType: z.string().nullable(),
  quantityUnit: UnitPriceMeasurementMeasuredUnitSchema.nullable(),
  quantityValue: z.number(),
  referenceUnit: UnitPriceMeasurementMeasuredUnitSchema.nullable(),
  referenceValue: z.number(),
});
export const UnitPriceMeasurementSchema: z.ZodType<UnitPriceMeasurement> =
  UnitPriceMeasurementSchema_;
export interface UnitPriceMeasurement extends z.infer<
  typeof UnitPriceMeasurementSchema_
> {}

const InventoryItemSchema_ = z.object({
  countryCodeOfOrigin: CountryCodeSchema.nullable(),
  hsCode: HsCodeSchema.nullable(),
  weight: ShopifyWeightSchema.nullable(),
  requiresShipping: z.boolean().nullable(),
  tracked: z.boolean().nullable(),
});
export const InventoryItemSchema: z.ZodType<InventoryItem> =
  InventoryItemSchema_;
export interface InventoryItem extends z.infer<typeof InventoryItemSchema_> {}
type InventoryItemWithoutRedoIds = Omit<InventoryItem, "hsCode"> & {
  hsCode: { code: string } | null;
};

export const ShopifyVariantListingSchema_ = BaseProductListingSchema.extend({
  listingType: z.literal(ListingType.Shopify),
  id: z.bigint(),
  shopifyVariantId: shopifyGidSchema(ShopifyIdType.ProductVariant).nullable(),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
  inventoryPolicy: ShopifyInventoryPolicySchema,
  price: z.number(),
  compareAtPrice: z.number().nullable(),
  inventoryItem: InventoryItemSchema,
  unitPrice: MoneySchema.nullable(),
  showUnitPrice: z.boolean().nullable(),
  unitPriceMeasurement: UnitPriceMeasurementSchema.nullable(),
  selectedOptions: z.array(ShopifyListingSelectedOptionSchema),
  media: z.array(ShopifyListingMediaSchema),
  metafields: z.array(ShopifyMetafieldSchema),
  taxable: z.boolean().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const ShopifyVariantListingSchema: z.ZodType<ShopifyVariantListing> =
  ShopifyVariantListingSchema_;
export interface ShopifyVariantListing extends z.infer<
  typeof ShopifyVariantListingSchema_
> {}

export type ShopifyVariantListingWithoutRedoIds = Omit<
  ShopifyVariantListing,
  "id" | "productId" | "media" | "inventoryItem"
> & {
  media: ShopifyListingMediaWithoutRedoIds[];
  inventoryItem: InventoryItemWithoutRedoIds;
};

export type ShopifyVariantListingWithShopifyVariantId = Omit<
  ShopifyVariantListing,
  "shopifyVariantId"
> & { shopifyVariantId: string };

export type ShopifyVariantListingWithShopifyIds = Omit<
  ShopifyVariantListing,
  "shopifyVariantId" | "media"
> & { shopifyVariantId: string; media: ShopifyListingMediaWithShopifyIds[] };

export const ShopifyVariantFieldTypeSchema = createEnum(
  ShopifyVariantFieldType,
);

const ShopifyVariantMappingSchema_ = z.object({
  fieldName: ShopifyVariantFieldTypeSchema,
  attributeDefinitionId: attributeDefinitionIdFormat.schema,
});
export const ShopifyVariantMappingSchema: z.ZodType<ShopifyVariantMapping> =
  ShopifyVariantMappingSchema_;
export interface ShopifyVariantMapping extends z.infer<
  typeof ShopifyVariantMappingSchema_
> {}

export const ShopifyVariantAttributeType = {
  [ShopifyVariantFieldType.sku]: AttributeType.ShortText,
  [ShopifyVariantFieldType.requires_shipping]: AttributeType.Boolean,
  [ShopifyVariantFieldType.country_of_origin]: AttributeType.Enum,
  [ShopifyVariantFieldType.hs_code]: AttributeType.HsCode,
  [ShopifyVariantFieldType.weight]: AttributeType.Weight,
  [ShopifyVariantFieldType.barcode]: AttributeType.ShortText,
  [ShopifyVariantFieldType.price]: AttributeType.Money,
  [ShopifyVariantFieldType.cost]: AttributeType.Money,
  [ShopifyVariantFieldType.compare_at_price]: AttributeType.Money,
  [ShopifyVariantFieldType.taxable]: AttributeType.Boolean,
  [ShopifyVariantFieldType.inventory_policy]: AttributeType.Enum,
  [ShopifyVariantFieldType.show_unit_price]: AttributeType.Boolean,
  [ShopifyVariantFieldType.upm_measured_type]: AttributeType.ShortText,
  [ShopifyVariantFieldType.upm_quantity_unit]: AttributeType.Enum,
  [ShopifyVariantFieldType.upm_quantity_value]: AttributeType.Float,
  [ShopifyVariantFieldType.upm_reference_unit]: AttributeType.Enum,
  [ShopifyVariantFieldType.upm_reference_value]: AttributeType.Float,
  [ShopifyVariantFieldType.display_name]: AttributeType.ShortText,
  [ShopifyVariantFieldType.title]: AttributeType.ShortText,
  [ShopifyVariantFieldType.metafield]: AttributeType.Json,
  [ShopifyVariantFieldType.inventory_tracked]: AttributeType.Boolean,
} satisfies Record<ShopifyVariantFieldType, AttributeType>;

export const ShopifyVariantAttributeSchema = {
  [ShopifyVariantFieldType.sku]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.sku]
    ].nullable(),
  [ShopifyVariantFieldType.requires_shipping]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.requires_shipping]
    ].nullable(),
  [ShopifyVariantFieldType.country_of_origin]:
    createSafeNullableEnumSchema(CountryCodeSchema),
  [ShopifyVariantFieldType.hs_code]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.hs_code]
    ].nullable(),
  [ShopifyVariantFieldType.weight]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.weight]
    ].nullable(),
  [ShopifyVariantFieldType.barcode]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.barcode]
    ].nullable(),
  [ShopifyVariantFieldType.price]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.price]
    ].nullable(),
  [ShopifyVariantFieldType.cost]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.cost]
    ].nullable(),
  [ShopifyVariantFieldType.compare_at_price]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.compare_at_price]
    ].nullable(),
  [ShopifyVariantFieldType.taxable]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.taxable]
    ].nullable(),
  [ShopifyVariantFieldType.inventory_policy]: createSafeNullableEnumSchema(
    ShopifyInventoryPolicySchema,
  ),
  [ShopifyVariantFieldType.show_unit_price]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.show_unit_price]
    ].nullable(),
  [ShopifyVariantFieldType.upm_measured_type]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.upm_measured_type]
    ].nullable(),
  [ShopifyVariantFieldType.upm_quantity_unit]: createSafeNullableEnumSchema(
    UnitPriceMeasurementMeasuredUnitSchema,
  ),
  [ShopifyVariantFieldType.upm_quantity_value]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.upm_quantity_value]
    ].nullable(),
  [ShopifyVariantFieldType.upm_reference_unit]: createSafeNullableEnumSchema(
    UnitPriceMeasurementMeasuredUnitSchema,
  ),
  [ShopifyVariantFieldType.upm_reference_value]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.upm_reference_value]
    ].nullable(),
  [ShopifyVariantFieldType.display_name]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.display_name]
    ].nullable(), // Soon to be deleted
  [ShopifyVariantFieldType.title]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.title]
    ].nullable(), // Soon to be deleted
  [ShopifyVariantFieldType.metafield]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.metafield]
    ].nullable(),
  [ShopifyVariantFieldType.inventory_tracked]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.inventory_tracked]
    ].nullable(),
};

export type ShopifyVariantAttributeValueType = {
  [key in ShopifyVariantFieldType]: (typeof ShopifyVariantAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getShopifyVariantAttributeValue<
  T extends ShopifyVariantFieldType,
>(field: T, value: unknown): ShopifyVariantAttributeValueType[T] | null {
  return ShopifyVariantAttributeSchema[field].parse(
    value,
  ) as ShopifyVariantAttributeValueType[T];
}

export function getShopifyVariantAttributeType<
  T extends ShopifyVariantFieldType,
>(field: T): (typeof ShopifyVariantAttributeType)[T] {
  return ShopifyVariantAttributeType[field];
}

export function getShopifyVariantListingAttributeValue<
  T extends ShopifyVariantFieldType,
>({
  listing,
  field,
}: {
  listing: ShopifyVariantListing;
  field: T;
}): AttributeValueType[AttributeType] | null {
  let fieldValue;
  switch (field) {
    case "sku":
      fieldValue = listing.sku;
      break;
    case "barcode":
      fieldValue = listing.barcode;
      break;
    case "requires_shipping":
      fieldValue = listing.inventoryItem.requiresShipping;
      break;
    case "country_of_origin":
      fieldValue = listing.inventoryItem.countryCodeOfOrigin;
      break;
    case "hs_code":
      fieldValue = listing.inventoryItem.hsCode;
      break;
    case "weight":
      fieldValue = listing.inventoryItem.weight;
      break;
    case "price":
      fieldValue = listing.price;
      break;
    case "cost":
      fieldValue = listing.unitPrice;
      break;
    case "compare_at_price":
      fieldValue = listing.compareAtPrice;
      break;
    case "taxable":
      fieldValue = listing.taxable;
      break;
    case "inventory_policy":
      fieldValue = listing.inventoryPolicy;
      break;
    case "show_unit_price":
      fieldValue = listing.showUnitPrice;
      break;
    case "upm_measured_type":
      fieldValue = listing.unitPriceMeasurement?.measuredType ?? null;
      break;
    case "upm_quantity_unit":
      fieldValue = listing.unitPriceMeasurement?.quantityUnit ?? null;
      break;
    case "upm_quantity_value":
      fieldValue = listing.unitPriceMeasurement?.quantityValue ?? null;
      break;
    case "upm_reference_unit":
      fieldValue = listing.unitPriceMeasurement?.referenceUnit ?? null;
      break;
    case "upm_reference_value":
      fieldValue = listing.unitPriceMeasurement?.referenceValue ?? null;
      break;
    case "display_name":
      fieldValue = null;
      break;
    case "title":
      fieldValue = null;
      break;
    case "metafield":
      fieldValue = null;
      break;
    case "inventory_tracked":
      fieldValue = listing.inventoryItem.tracked;
      break;
    default:
      assertNever(field);
  }

  return ShopifyVariantAttributeSchema[field].parse(fieldValue);
}
